import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMatches } from '../../hooks/useMatches'
import { useTeams } from '../../hooks/useTeams'
import { usePoules } from '../../hooks/usePoules'
import { Button, Card, Badge, DarkModeToggleCompact } from '../../components/ui'
import { Printer, ArrowLeft, FileText, List, Calendar, Download, Sparkles, CheckCircle2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'

export const ReportsPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { tournamentId } = useParams()
    const { matches } = useMatches(tournamentId)
    const { teams } = useTeams(tournamentId)
    const { poules } = usePoules(tournamentId)

    const [tournament, setTournament] = useState(null)
    const [activeReport, setActiveReport] = useState('schedule') // 'schedule', 'teams', 'poules', 'scoreforms'
    const [sportType, setSportType] = useState('goals')

    useEffect(() => {
        const fetchTourney = async () => {
            const { data } = await supabase.from('tournaments').select('*').eq('id', tournamentId).single()
            if (data) {
                setTournament(data)
                setSportType(data.sport_type || 'goals')
            }
        }
        fetchTourney()
    }, [tournamentId])

    const handlePrint = () => {
        window.print()
    }

    const matchesByDate = matches.reduce((acc, match) => {
        const date = match.match_date || t('reports_page.unknown_date')
        if (!acc[date]) acc[date] = []
        acc[date].push(match)
        return acc
    }, {})

    Object.keys(matchesByDate).forEach(date => {
        matchesByDate[date].sort((a, b) => (a.match_time || '').localeCompare(b.match_time || ''))
    })

    const getPouleName = (pId) => poules.find(p => p.id === pId)?.name || '?'
    const isEsports = sportType === 'esports'

    return (
        <div className="min-h-screen">
            {/* GLOBAL PRINT STYLES */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    #printable-area, #printable-area * { visibility: visible; }
                    #printable-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        background: white !important;
                        color: black !important;
                        margin: 0;
                        padding: 0;
                        font-family: 'Inter', sans-serif;
                    }
                    .no-print { display: none !important; }
                    * { box-shadow: none !important; text-shadow: none !important; }
                    .page-break { page-break-before: always; }
                    table { page-break-inside: auto; width: 100%; border-collapse: collapse; }
                    tr { page-break-inside: avoid; page-break-after: auto; }
                    th { border-bottom: 2px solid #000; text-align: left; }
                    td { border-bottom: 1px solid #ddd; }
                }
            `}</style>

            <div className="premium-container animate-fadeIn">
                {/* Hero Section */}
                <div className="mb-8 animate-slideUp no-print">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/admin/dashboard')} 
                        className={`mb-4 ${isEsports ? 'text-slate-300 hover:text-white hover:bg-white/5' : ''}`}
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        {t('reports_page.btn_back')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className={`p-3.5 rounded-2xl shadow-lg transition-all duration-500 hover:rotate-12 ${
                                isEsports 
                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' 
                                    : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_0_20px_rgba(79,70,229,0.3)]'
                            }`}>
                                <Printer className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className={`text-5xl font-bold leading-tight ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    {t('reports_page.title')}
                                </h1>
                                <p className={`text-lg mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>
                                    {t('reports_page.subtitle')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <DarkModeToggleCompact />
                            <Button 
                                onClick={handlePrint} 
                                variant="primary" 
                                className={`shadow-lg font-bold flex items-center gap-2 py-3.5 px-6 rounded-xl ${
                                    isEsports ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]' : ''
                                }`}
                            >
                                <Printer size={20} />
                                {t('reports_page.btn_print')}
                            </Button>
                        </div>
                    </div>

                    {/* Report Selector Tabs */}
                    <div className="flex gap-4 border-b border-gray-200/20 pb-1 mb-6">
                        <button
                            onClick={() => setActiveReport('schedule')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeReport === 'schedule' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Calendar size={18} />
                            {t('reports_page.tab_schedule')}
                        </button>
                        <button
                            onClick={() => setActiveReport('poules')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeReport === 'poules' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <List size={18} />
                            {t('reports_page.tab_poules')}
                        </button>
                        <button
                            onClick={() => setActiveReport('teams')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeReport === 'teams' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <FileText size={18} />
                            {t('reports_page.tab_teams')}
                        </button>
                        <button
                            onClick={() => setActiveReport('scoreforms')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeReport === 'scoreforms' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <List size={18} className="rotate-90" />
                            {t('reports_page.tab_scoreforms')}
                        </button>
                    </div>
                </div>

                {/* PREVIEW CONTAINER */}
                <div className="grid grid-cols-1 gap-8 animate-slideUp">
                    {/* Live Preview Paper Wrapper */}
                    <div className={`p-8 rounded-3xl border shadow-xl flex flex-col items-center ${
                        isEsports ? 'bg-purple-950/10 border-purple-500/20 shadow-[0_0_35px_rgba(168,85,247,0.06)]' : 'bg-white border-gray-100'
                    }`}>
                        <div className="w-full flex items-center justify-between mb-6 pb-4 border-b border-gray-200/10 text-left no-print">
                            <div>
                                <h3 className={`text-xl font-bold flex items-center gap-2 ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    <Sparkles size={20} className={isEsports ? 'text-purple-400' : 'text-indigo-500'} />
                                    Afdrukvoorbeeld (A4-formaat)
                                </h3>
                                <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>Dit is exact hoe de PDF-rapportage geprint zal worden.</p>
                            </div>
                        </div>

                        {/* Printable Area with Watermark */}
                        <div
                            id="printable-area"
                            className="bg-white w-full max-w-[210mm] min-h-[297mm] p-[15mm] md:p-[20mm] shadow-2xl rounded-2xl text-black relative overflow-hidden"
                        >
                            {/* WATERMARK */}
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] overflow-hidden">
                                <div className="text-[180px] font-bold -rotate-45 whitespace-nowrap tracking-tighter">PROPOULE</div>
                            </div>

                            {/* REPORT HEADER */}
                            <div className="border-b-4 border-black pb-6 mb-8 flex justify-between items-end relative z-10">
                                <div>
                                    <h1 className="text-4xl font-black uppercase tracking-tighter leading-none mb-2">
                                        {tournament?.name || 'Toernooi'}
                                    </h1>
                                    <div className="flex items-center gap-3">
                                        <span className="bg-black text-white px-3 py-1 font-bold text-sm uppercase">{t('reports_page.report_label')}</span>
                                        <span className="text-gray-600 font-bold uppercase tracking-wide">
                                            {activeReport === 'schedule' && t('reports_page.tab_schedule')}
                                            {activeReport === 'poules' && t('reports_page.tab_poules')}
                                            {activeReport === 'teams' && t('reports_page.tab_teams')}
                                            {activeReport === 'scoreforms' && t('reports_page.tab_scoreforms')}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs uppercase font-bold text-gray-400">{t('reports_page.generated_on')}</p>
                                    <p className="font-mono font-bold text-lg">{new Date().toLocaleDateString(t('common.date_locale', { defaultValue: 'nl-NL' }))}</p>
                                    <p className="font-mono text-sm">{new Date().toLocaleTimeString(t('common.date_locale', { defaultValue: 'nl-NL' }))}</p>
                                </div>
                            </div>

                            {/* REPORT CONTENT */}
                            <div className="relative z-10">
                                {activeReport === 'schedule' && (
                                    <div className="space-y-8">
                                        {Object.entries(matchesByDate).map(([date, dayMatches]) => (
                                            <div key={date}>
                                                <h3 className="text-lg font-bold bg-black text-white px-3 py-1 mb-4 inline-block uppercase tracking-wide">
                                                    {date}
                                                </h3>
                                                <div className="border-t-2 border-black">
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="border-b border-black text-left text-black uppercase text-xs">
                                                                <th className="py-2 pl-2 w-20 font-black">{t('reports_page.header_time')}</th>
                                                                <th className="py-2 w-24 font-black">{t('reports_page.header_field')}</th>
                                                                <th className="py-2 font-black">{t('reports_page.header_match')}</th>
                                                                <th className="py-2 w-32 font-black">{t('reports_page.header_info')}</th>
                                                                <th className="py-2 text-right pr-2 w-24 font-black">{t('reports_page.header_score')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-200">
                                                            {dayMatches.map((match, i) => (
                                                                <tr key={match.id} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                    <td className="py-3 pl-2 font-bold font-mono text-base">{match.match_time?.slice(0, 5) || '--:--'}</td>
                                                                    <td className="py-3 text-gray-800 font-bold uppercase text-xs tracking-wide">{match.field || '-'}</td>
                                                                    <td className="py-3 font-semibold text-lg">
                                                                        {match.home_team?.name || 'Home'} <span className="text-gray-400 text-sm mx-1 italic">vs</span> {match.away_team?.name || 'Away'}
                                                                    </td>
                                                                    <td className="py-3 text-xs text-gray-500 uppercase font-bold">
                                                                        {match.poule_id ? getPouleName(match.poule_id) : (match.round_name || match.type)}
                                                                    </td>
                                                                    <td className="py-3 text-right pr-2 font-mono font-bold text-lg bg-gray-100 border-l border-gray-200">
                                                                        {match.score_home !== null ? `${match.score_home} - ${match.score_away}` : ''}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ))}
                                        {matches.length === 0 && <p className="text-gray-500 italic">{t('reports_page.no_matches')}</p>}
                                    </div>
                                )}

                                {activeReport === 'poules' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {poules.map(poule => {
                                            const pouleTeams = teams.filter(t => t.poule_id === poule.id)
                                            return (
                                                <div key={poule.id} className="break-inside-avoid border-2 border-black rounded-lg overflow-hidden">
                                                    <div className="bg-black text-white p-3 flex justify-between items-center">
                                                        <h3 className="font-black text-xl tracking-tight uppercase">{poule.name}</h3>
                                                        <span className="text-xs bg-white text-black px-2 py-1 rounded font-bold">{t('reports_page.teams_count', { count: pouleTeams.length })}</span>
                                                    </div>
                                                    <div className="bg-white">
                                                        <table className="w-full text-sm">
                                                            <tbody className="divide-y divide-gray-200">
                                                                {pouleTeams.map((team, idx) => (
                                                                    <tr key={team.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                        <td className="py-3 px-4 font-black text-gray-400 w-8 border-r border-gray-200">{idx + 1}</td>
                                                                        <td className="py-3 px-4 font-bold text-lg text-gray-900">{team.name}</td>
                                                                    </tr>
                                                                ))}
                                                                {pouleTeams.length === 0 && (
                                                                    <tr><td colSpan="2" className="p-4 text-center text-gray-400 italic">{t('reports_page.no_teams_in_poule')}</td></tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {poules.length === 0 && <p className="text-gray-500 italic">{t('reports_page.no_poules')}</p>}
                                    </div>
                                )}

                                {activeReport === 'teams' && (
                                    <div>
                                        <table className="w-full text-sm border-2 border-black">
                                            <thead className="bg-black text-white">
                                                <tr className="text-left uppercase text-xs font-black">
                                                    <th className="py-3 px-4 border-b border-black text-white">{t('reports_page.header_team')}</th>
                                                    <th className="py-3 px-4 border-b border-black text-white">{t('reports_page.header_poule_layout')}</th>
                                                    <th className="py-3 px-4 border-b border-black text-white text-right">{t('reports_page.header_reg_info')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {teams.sort((a, b) => a.name.localeCompare(b.name)).map((team, idx) => (
                                                    <tr key={team.id} className={`hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                                        <td className="py-3 px-4 font-bold text-lg text-gray-900 border-r border-gray-200">{team.name}</td>
                                                        <td className="py-3 px-4 text-gray-800 font-medium">
                                                            {team.poule_id ? (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gray-200 text-gray-800 uppercase tracking-wide">
                                                                    {getPouleName(team.poule_id)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400 italic text-xs">{t('reports_page.not_assigned')}</span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-4 text-right">
                                                            <span className="font-mono text-xs text-gray-400">ID: {team.id.slice(0, 6)}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div className="mt-4 flex justify-end">
                                            <div className="bg-black text-white px-4 py-2 font-bold rounded">
                                                {t('reports_page.total_teams', { count: teams.length })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeReport === 'scoreforms' && (
                                    <div className="grid grid-cols-2 gap-8">
                                        {matches
                                            .sort((a, b) => (a.match_time || '').localeCompare(b.match_time || ''))
                                            .slice(0, 8) 
                                            .map((match) => (
                                                <div key={match.id} className="break-inside-avoid border-2 border-black p-4 relative bg-white">
                                                    <div className="flex justify-between items-start border-b-2 border-black pb-2 mb-4">
                                                        <div>
                                                            <div className="text-xs font-black uppercase text-gray-500">{t('reports_page.sf_time_field')}</div>
                                                            <div className="text-xl font-black">
                                                                {match.match_time ? match.match_time.slice(0, 5) : '--:--'} <span className="mx-1 text-gray-300">|</span> {match.field || '?'}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-xs font-black uppercase text-gray-500">{t('reports_page.sf_match_id')}</div>
                                                            <div className="font-mono text-sm">{match.category || '#'}{match.id.slice(0, 4)}</div>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-between items-center mb-6">
                                                        <div className="w-5/12">
                                                            <div className="text-lg font-bold leading-tight mb-2">{match.home_team?.name || 'Home'}</div>
                                                        </div>
                                                        <div className="w-2/12 text-center font-black text-2xl text-gray-300">VS</div>
                                                        <div className="w-5/12 text-right">
                                                            <div className="text-lg font-bold leading-tight mb-2">{match.away_team?.name || 'Away'}</div>
                                                        </div>
                                                    </div>

                                                    <div className="flex gap-4 border-t-2 border-gray-100 pt-4">
                                                        <div className="w-1/2 border-2 border-gray-300 h-16 rounded flex items-start justify-center p-1">
                                                            <span className="text-[10px] uppercase font-bold text-gray-400">{t('reports_page.sf_score_home')}</span>
                                                        </div>
                                                        <div className="w-1/2 border-2 border-gray-300 h-16 rounded flex items-start justify-center p-1">
                                                            <span className="text-[10px] uppercase font-bold text-gray-400">{t('reports_page.sf_score_away')}</span>
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 pt-2 border-t border-dashed border-gray-300 flex justify-between text-xs text-gray-400">
                                                        <span>{t('reports_page.sf_signature')}</span>
                                                        <span className="w-32 border-b border-gray-300"></span>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>

                            {/* REPORT FOOTER */}
                            <div className="mt-20 pt-4 border-t-4 border-black flex justify-between items-center text-xs font-bold text-gray-400 uppercase tracking-widest relative z-10">
                                <span>{t('reports_page.footer_powered')}</span>
                                <span>{t('reports_page.footer_page')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
